import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--file-field"
//
// Turns a native file input into a dropzone: aceita clique e arrastar-e-soltar,
// lista os arquivos escolhidos e permite remover cada um deles.
//
// Values:
//   multiple: permite acumular vários arquivos (espelha o atributo multiple do input)
//   accept:   lista de tipos/extensões aceitos, no mesmo formato do atributo accept
export default class extends Controller {
  static targets = ["input", "dropzone", "list", "template", "rejected"]

  static values = {
    multiple: { type: Boolean, default: false },
    accept: { type: String, default: "" }
  }

  connect() {
    this.files = Array.from(this.inputTarget.files || [])
    this.render()

    // Progresso do upload direto (Active Storage). Os eventos são disparados no
    // próprio input e sobem por bubbling até este wrapper.
    this.boundUploadStart = this.uploadStart.bind(this)
    this.boundUploadProgress = this.uploadProgress.bind(this)
    this.boundUploadError = this.uploadError.bind(this)
    this.boundUploadEnd = this.uploadEnd.bind(this)
    this.element.addEventListener("direct-upload:start", this.boundUploadStart)
    this.element.addEventListener("direct-upload:progress", this.boundUploadProgress)
    this.element.addEventListener("direct-upload:error", this.boundUploadError)
    this.element.addEventListener("direct-upload:end", this.boundUploadEnd)
  }

  disconnect() {
    this.element.removeEventListener("direct-upload:start", this.boundUploadStart)
    this.element.removeEventListener("direct-upload:progress", this.boundUploadProgress)
    this.element.removeEventListener("direct-upload:error", this.boundUploadError)
    this.element.removeEventListener("direct-upload:end", this.boundUploadEnd)
  }

  change() {
    this.add(Array.from(this.inputTarget.files || []))
  }

  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-primary-500", "bg-primary-50")
  }

  dragleave(event) {
    event.preventDefault()

    // dragleave também dispara ao passar sobre elementos filhos da zona
    if (event.currentTarget.contains(event.relatedTarget)) return

    this.clearDragState()
  }

  drop(event) {
    event.preventDefault()
    this.clearDragState()
    this.add(Array.from(event.dataTransfer?.files || []))
  }

  remove(event) {
    event.preventDefault()

    const index = Number(event.currentTarget.closest("[data-file-index]").dataset.fileIndex)
    this.files.splice(index, 1)
    this.syncInput()
    this.render()
  }

  // private

  add(incoming) {
    const accepted = incoming.filter(file => this.accepted(file))

    this.showRejected(incoming.length - accepted.length)

    if (accepted.length === 0) {
      this.syncInput()
      return
    }

    this.files = this.multipleValue ? this.files.concat(accepted) : accepted.slice(0, 1)
    this.syncInput()
    this.render()
  }

  // Reescreve o FileList do input a partir da lista atual, para que o submit
  // envie exatamente os arquivos exibidos.
  syncInput() {
    const transfer = new DataTransfer()
    this.files.forEach(file => transfer.items.add(file))
    this.inputTarget.files = transfer.files
  }

  render() {
    this.listTarget.replaceChildren()

    this.files.forEach((file, index) => {
      const item = this.templateTarget.content.firstElementChild.cloneNode(true)

      item.dataset.fileIndex = index
      item.querySelector("[data-file-name]").textContent = file.name
      item.querySelector("[data-file-size]").textContent = this.humanSize(file.size)

      this.listTarget.appendChild(item)
    })

    const empty = this.files.length === 0

    this.listTarget.classList.toggle("hidden", empty)
    // display inline: a classe `hidden` empataria em especificidade com o `flex` da zona
    this.dropzoneTarget.style.display = (!empty && !this.multipleValue) ? "none" : ""
    this.dropzoneTarget.classList.toggle("mb-2", this.multipleValue && !empty)
  }

  // Localiza o <li> correspondente ao arquivo do evento de upload.
  liFor(file) {
    const index = this.files.indexOf(file)
    if (index === -1) return null

    return this.listTarget.querySelector(`[data-file-index="${index}"]`)
  }

  uploadStart(event) {
    const li = this.liFor(event.detail.file)
    if (!li) return

    li.querySelector("[data-file-progress]").classList.remove("hidden")
    li.querySelector("[data-file-percent]").classList.remove("hidden")
    li.querySelector("[data-file-spinner]").classList.remove("hidden")
    li.querySelector("[data-file-remove]").classList.add("hidden")
  }

  uploadProgress(event) {
    const li = this.liFor(event.detail.file)
    if (!li) return

    const percent = Math.round(event.detail.progress)
    li.querySelector("[data-file-bar]").style.width = `${percent}%`
    li.querySelector("[data-file-percent]").textContent = `${percent}%`
  }

  uploadError(event) {
    const li = this.liFor(event.detail.file)
    if (!li) return

    li.dataset.fileFailed = "true"
    li.querySelector("[data-file-spinner]").classList.add("hidden")
    li.querySelector("[data-file-percent]").classList.add("hidden")

    const bar = li.querySelector("[data-file-bar]")
    bar.classList.remove("bg-primary-600")
    bar.classList.add("bg-red-500")

    const error = li.querySelector("[data-file-error]")
    // Mensagem honesta: a causa mais comum é o storage inalcançável, e um simples
    // "tente novamente" já levou advogados a repetir o envio 4 vezes em vão.
    error.textContent = "Não foi possível enviar este arquivo. Tente novamente; se o erro persistir, o serviço de arquivos pode estar indisponível."
    error.classList.remove("hidden")
  }

  uploadEnd(event) {
    const li = this.liFor(event.detail.file)
    if (!li) return

    li.querySelector("[data-file-spinner]").classList.add("hidden")

    if (li.dataset.fileFailed !== "true") {
      li.querySelector("[data-file-percent]").classList.add("hidden")
      li.querySelector("[data-file-done]").classList.remove("hidden")
    }
  }

  accepted(file) {
    if (this.acceptValue === "") return true
    // o navegador nem sempre consegue determinar o tipo; a validação no servidor decide
    if (file.type === "") return true

    const name = file.name.toLowerCase()

    return this.acceptValue.split(",").map(rule => rule.trim().toLowerCase()).some(rule => {
      if (rule === "") return false
      if (rule.startsWith(".")) return name.endsWith(rule)
      if (rule.endsWith("/*")) return file.type.startsWith(rule.slice(0, -1))
      return file.type === rule
    })
  }

  showRejected(count) {
    if (count > 0) {
      this.rejectedTarget.textContent = count === 1 ?
        "1 arquivo foi ignorado por não estar em um formato aceito." :
        `${count} arquivos foram ignorados por não estarem em um formato aceito.`
    }

    this.rejectedTarget.classList.toggle("hidden", count === 0)
  }

  clearDragState() {
    this.dropzoneTarget.classList.remove("border-primary-500", "bg-primary-50")
  }

  humanSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
}
